<script lang="ts" setup>
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    /**
     * URL for image.
     */
    src: string
    /**
     * Frame width.
     */
    width: number
    /**
     * Frame height.
     */
    height: number
    /**
     * Original image width.
     */
    originalImageWidth: number
    /**
     * Original image height.
     */
    originalImageHeight: number
    /**
     * Alt text for the image.
     */
    alt?: string
    /**
     * Whether to load the image lazily.
     */
    lazyLoading?: boolean
    /**
     * Whether to process the image before loading.
     */
    processImage?: boolean
    /**
     * Whether the theme is dark.
     */
    isDark?: boolean
    /**
     * Placeholder color for the thumbnail.
     */
    placeholderColor?: string
    /**
     * Placeholder 'background-color' value.
     */
    placeholderColorValue?: string
    /**
     * Fetch priority for image
     */
    fetchPriority?: 'auto' | 'high' | 'low'
  }>(),
  {
    alt: '',
    lazyLoading: true,
    processImage: false,
    isDark: false,
    placeholderColor: 'transparent',
    placeholderColorValue: undefined,
    fetchPriority: 'auto',
  },
)

const emit = defineEmits<{
  (e: 'load'): void
  (e: 'error', error: Error): void
}>()

const originalImageAspectRatio = computed(() => props.originalImageWidth / props.originalImageHeight)
const containerAspectRatio = computed(() => props.width / props.height)

const imageDimensions = computed(() => {
  let imageWidth: number
  let imageHeight: number

  if (originalImageAspectRatio.value > containerAspectRatio.value) {
    // Image is wider than container - fit to width
    imageWidth = props.width
    imageHeight = props.width / originalImageAspectRatio.value
  } else {
    // Image is taller than container - fit to height
    imageHeight = props.height
    imageWidth = props.height * originalImageAspectRatio.value
  }

  return {
    width: imageWidth,
    height: imageHeight,
  }
})

const backgroundDimensions = computed(() => {
  const isLandscape = originalImageAspectRatio.value > 1

  let bgWidth: number
  let bgHeight: number

  if (isLandscape) {
    // For landscape images, ensure 32px padding on vertical sides
    bgHeight = props.height + 64 // 32px padding on each vertical side
    bgWidth = bgHeight * originalImageAspectRatio.value
  } else {
    // For portrait images, ensure 32px padding on horizontal sides
    bgWidth = props.width + 64 // 32px padding on each horizontal side
    bgHeight = bgWidth / originalImageAspectRatio.value
  }

  return {
    width: bgWidth,
    height: bgHeight,
  }
})

const xBlurredBackground = computed(() => originalImageAspectRatio.value !== containerAspectRatio.value)
</script>

<template>
  <div
    class="relative grid place-items-center overflow-hidden rounded-[2px]"
    :style="{ width: `${width}px`, height: `${height}px` }"
  >
    <!-- Blurred background image -->
    <div
      v-if="xBlurredBackground"
      class="absolute inset-0 flex items-center justify-center overflow-hidden"
      :class="isDark ? 'bg-black/70' : 'bg-black/30'"
    >
      <ImageThumbnail
        :src="src"
        :width="backgroundDimensions.width"
        :height="backgroundDimensions.height"
        :original-image-width="originalImageWidth"
        :original-image-height="originalImageHeight"
        :process-image="processImage"
        :lazy-loading="lazyLoading"
        :fetch-priority="fetchPriority"
        class="blur-[20px]"
        aria-hidden="true"
      />
    </div>

    <!-- Main centered image -->
    <div class="items-center justify-center">
      <ImageThumbnail
        :src="src"
        :width="imageDimensions.width"
        :height="imageDimensions.height"
        :original-image-width="originalImageWidth"
        :original-image-height="originalImageHeight"
        :alt="alt"
        :process-image="processImage"
        :lazy-loading="lazyLoading"
        :placeholder-color="placeholderColor"
        :placeholder-color-value="placeholderColorValue"
        :fetch-priority="fetchPriority"
        class="rounded-[2px]"
        @load="emit('load')"
        @error="emit('error', $event)"
      />
    </div>
  </div>
</template>
